import * as THREE from 'three';
import { C, SEASON_LEAF } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, breadSlice, stickLegs, checkNpcClick } from '../entities/townsfolk.js';
import { createAmbient } from '../entities/ambient.js';
import { HUD } from '../ui/hud.js';
import { addCrumbs, addLumber, getFlag, setFlag } from '../core/save.js';
import {
  questsForGiver, acceptQuest, turnIn, progress, flagObjective,
  isReady, objectiveProgress, QUESTS, isActive
} from '../core/quests.js';
import { addFound, randomFind, ITEMS } from '../core/collection.js';
import { addIngredient, ingredientName } from '../core/ingredients.js';
import { advanceTime, getSeason, isNight } from '../core/world.js';
import { WeatherSystem, getWeatherState } from '../core/weather.js';
import { spendForAction } from '../core/actions.js';
import { Woodcut } from '../minigames/woodcut.js';

// The Forest — Toast Town's first wild zone and the gateway to the RPG layer.
// Phase-3 slice 1: an explorable wood you reach from the town's west path. Trees,
// a dirt trail up to a scaffolded Fortress deep in the woods, sparkly dig spots,
// a big choppable tree, and a few forest critters. Wood-cutting, the quest
// system, and the Fortress interior are later slices — the walk-ups flag them.

export function createForestScene(){
  return () => new ForestScene();
}

const GROUND_R = 34;      // how far the forest floor is drawn
const WALK_R   = 26;      // how far you may walk — the tree wall stands here

const LAMP_ON  = 0xFFE9A8;
const LAMP_OFF = 0x8A8A7A;

const OAK_POS  = new THREE.Vector3(12, 0, -6);
const CHOP_SPOT = new THREE.Vector3(10, 0, -4);   // by the stump, facing the oak

class ForestScene {
  constructor(){
    this.state = 'explore';
    this.woodcut = null;
    this.panels = [];
    this.chips = [];
    this.breadChopT = 0;
    this.oakPop = 0;
    this.lamps = [];

    this.season = getSeason();
    this.leaf = SEASON_LEAF[this.season] || C.sprout;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x9FC98A);
    this.scene.fog = new THREE.Fog(0x9FC98A, 38, 92);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 200);
    this.camDist = 18;
    this.camTarget = new THREE.Vector3(0, 1.6, 4);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildGround();
    this._buildForest();
    this._buildFortress();
    const digSpots = this._buildDigSpots();
    this._buildKeystoneSpots();
    this._buildWoodcut();
    this._buildGraveyardGate();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 14);
    this.bread.root.rotation.y = Math.PI;                 // facing into the woods
    this.scene.add(this.bread.root);

    // a few forest critters ambling in the underbrush
    this.critters = this._makeCritters();
    this.critters.forEach(c => this.scene.add(c.root));

    // Ranger Rusk — the forest's quest-giver, posted by the entry clearing so you
    // meet him the moment you arrive. He hands out the woodland quest chain.
    this.ranger = this._makeRanger();
    this.scene.add(this.ranger.root);

    this.ambient = createAmbient({ center: new THREE.Vector3(0, 0, -3), radius: 22, butterflies: 5, birds: 2, fireflies: 6 });
    this.scene.add(this.ambient.group);

    this.weather = new WeatherSystem(this.scene, { center: new THREE.Vector3(0, 0, -3), radius: WALK_R });
    this._applyWeather();

    this.hotspots = [
      { pos: this.ranger.root.position.clone(), r: 2.6, label: '🗣 talk to Ranger Rusk', action: () => this._talkToRanger() },
      { pos: OAK_POS.clone(), r: 2.8, label: '🪓 chop the big oak', action: () => this._startWoodcut() },
      { pos: new THREE.Vector3(0, 0, -13), r: 3.4, label: '🏰 approach the Fortress', action: () => this._approachFortress() },
      ...digSpots.map((p) => ({ pos: p, r: 1.9, label: '✨ dig here', action: () => this._dig(p) })),
      ...this.keystoneSpots.map((s, i) => ({
        pos: s.pos, r: 1.9,
        label: s.dug ? '⚱️ empty sigil mound' : '⚱️ dig the sigil mound',
        action: () => this._digKeystone(i)
      })),
      { pos: new THREE.Vector3(-18, 0, 4), r: 2.6, label: '🪦 enter the Graveyard', action: () => this._enterGraveyard() },
      { pos: new THREE.Vector3(0, 0, 18), r: 3.0, label: '← back to Toast Town', action: () => this.manager.goTo('town', { from: 'forest' }) }
    ];
    this.nearSpot = null;
    this._updateLamps();
  }

  // Ranger Rusk: a bread-slice villager in an olive campaign hat + neckerchief.
  _makeRanger(){
    const build = (g) => {
      breadSlice(g, C.crust, C.toast, 0.62, 1.1);
      sphere(.07, 0x2E2018, -.17, 1.18, .3, g, 8);          // eyes
      sphere(.07, 0x2E2018,  .17, 1.18, .3, g, 8);
      box(.34, .06, .05, 0x2E2018, 0, 1.02, .32, g);         // friendly smile
      const hat = new THREE.Group(); hat.position.set(0, 1.74, 0); g.add(hat);
      cyl(.5, .5, .06, 0x5A6B32, 0, 0, 0, hat, 18);          // wide brim
      cyl(.28, .3, .34, 0x5A6B32, 0, .2, 0, hat, 16);        // crown
      cyl(.31, .31, .06, 0x3E4A22, 0, .06, 0, hat, 16);      // band
      const scarf = box(.5, .12, .5, 0x3D8C5A, 0, .72, 0, g); scarf.rotation.y = .2;  // neckerchief
      stickLegs(g, .5);
    };
    return new Townsperson({
      name: 'Ranger Rusk', line: 'welcome to the woods, Bread Boi!',
      pos: new THREE.Vector3(3.5, 0, 12.5), facing: Math.PI * 0.9,
      giver: 'ranger',
      wander: 0, speed: 0, bubbleY: 2.4, build
    });
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xEAF3D8, 0.68));
    const sun = new THREE.DirectionalLight(0xFFF4D6, 0.85);
    sun.position.set(8, 20, 10); sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 38;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
    const fill = new THREE.DirectionalLight(0x9EC08A, 0.22);
    fill.position.set(-8, 6, 4); this.scene.add(fill);
  }

  _applyWeather(){
    const st = getWeatherState();
    this.weather.set(st.weather, st.season);
  }

  _buildGround(){
    // one ground material for the wild forest floor
    const floor = new THREE.Mesh(new THREE.CircleGeometry(GROUND_R, 64), new THREE.MeshLambertMaterial({ color: C.grass }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);

    // one path material: the trail winding from town up to the fortress
    const trail = new THREE.Mesh(new THREE.PlaneGeometry(4, 42), new THREE.MeshLambertMaterial({ color: C.path }));
    trail.rotation.x = -Math.PI/2; trail.position.set(0, 0.01, -2); trail.receiveShadow = true; this.scene.add(trail);
    // a small entry clearing
    const clearing = new THREE.Mesh(new THREE.CircleGeometry(4.6, 32), new THREE.MeshLambertMaterial({ color: C.path }));
    clearing.rotation.x = -Math.PI/2; clearing.position.set(0, 0.011, 14); this.scene.add(clearing);
  }

  _tree(x, z, s, dark = false){
    const g = new THREE.Group(); g.position.set(x, 0, z); g.scale.setScalar(s); this.scene.add(g);
    cyl(0.18, 0.24, 1.4, C.rye, 0, 0.7, 0, g, 8);                 // trunk
    const leaf = dark ? this._darkLeaf() : this.leaf;
    // pine (stacked cones) vs leafy (spheres), chosen by a positional hash
    if ((Math.floor(x * 7 + z * 13) & 1) === 0){
      cyl(0, 1.1, 1.7, leaf,   0, 2.0, 0, g, 9);
      cyl(0, 0.85, 1.4, this._darkLeaf(), 0, 2.9, 0, g, 9);
      cyl(0, 0.6,  1.1, leaf,   0, 3.7, 0, g, 9);
    } else {
      sphere(1.15, leaf,   0, 2.2, 0, g, 10);
      sphere(0.8, C.sprout, 0.55, 2.8, 0.3, g, 9);
      sphere(0.75, this._darkLeaf(), -0.55, 2.5, -0.2, g, 9);
    }
    return g;
  }

  _darkLeaf(){
    // a slightly darker variant of the season colour for depth
    const base = new THREE.Color(this.leaf);
    base.multiplyScalar(0.82);
    return base.getHex();
  }

  // Deterministic scatter (stable per load) that keeps the trail corridor, the
  // entry clearing, and the fortress apron clear. Then a dense, irregular outer
  // ring at the walk limit so the forest feels enclosed rather than a disc in a
  // field.
  _buildForest(){
    // inner grove
    for (let gx = -20; gx <= 20; gx += 3.5){
      for (let gz = -18; gz <= 12; gz += 3.5){
        const x = gx + Math.sin(gx * 3.1 + gz * 1.7) * 1.1;
        const z = gz + Math.cos(gx * 2.3 + gz * 4.1) * 1.1;
        if (Math.abs(x) < 3.2 && z > -16) continue;              // keep the trail open
        if (Math.hypot(x, z + 18) < 6.5) continue;               // fortress apron
        if (Math.hypot(x, z - 14) < 4.6) continue;               // entry clearing
        if (Math.hypot(x, z) > 22) continue;                     // leave room for the wall
        this._tree(x, z, 0.9 + Math.abs(Math.sin(gx * 1.3 + gz)) * 0.6);
      }
    }

    // irregular tree wall on the walk line
    const WALL_COUNT = 92;
    for (let i = 0; i < WALL_COUNT; i++){
      const a = (i / WALL_COUNT) * Math.PI * 2 + Math.sin(i * 4.7) * 0.35;
      const r = WALK_R - 1.2 + Math.cos(i * 3.1) * 1.8;
      const x = Math.sin(a) * r;
      const z = Math.cos(a) * r;
      // leave the town-exit gap on the south axis and the graveyard path on the west
      const southExit = Math.abs(Math.sin(a)) < 0.22 && Math.cos(a) > 0;
      const gravePath = a > 2.4 && a < 3.0 && r > WALK_R - 1.0;
      if (southExit || gravePath) continue;
      this._tree(x, z, 1.1 + Math.sin(i * 2.3) * 0.35, true);
    }

    // lamps along the trail and at the two exits
    this._lamp(new THREE.Vector3(0, 0, 16));
    this._lamp(new THREE.Vector3(-2.4, 0, 6));
    this._lamp(new THREE.Vector3(2.4, 0, -4));
    this._lamp(new THREE.Vector3(0, 0, -12));
    this._lamp(new THREE.Vector3(-16, 0, 4));
  }

  _lamp(pos){
    const g = new THREE.Group();
    g.position.copy(pos);
    this.scene.add(g);
    cyl(0.1, 0.14, 0.3, C.crustDark, 0, 0.15, 0, g, 8);
    cyl(0.05, 0.05, 3.0, C.rye, 0, 1.65, 0, g, 6);
    box(0.28, 0.28, 0.28, C.crustDark, 0, 3.28, 0, g);
    const glass = sphere(0.22, LAMP_ON, 0, 3.25, 0, g, 10);
    glass.material = new THREE.MeshBasicMaterial({ color: LAMP_OFF });
    const light = new THREE.PointLight(LAMP_ON, 0, 9, 1.7);
    light.position.set(0, 3.2, 0); g.add(light);
    this.lamps.push({ light, glass });
  }

  _updateLamps(){
    const night = isNight();
    this.lamps.forEach(l => {
      l.light.intensity = night ? 2.2 : 0;
      l.glass.material.color.set(night ? LAMP_ON : LAMP_OFF);
    });
  }

  // A stone stronghold at the back of the woods — scaffolded now, opened later.
  _buildFortress(){
    const f = new THREE.Group(); f.position.set(0, 0, -18); this.scene.add(f);
    box(11, 5.2, 4.2, C.stone, 0, 2.6, 0, f);                      // curtain wall
    box(11.5, 0.85, 4.7, C.stoneDim, 0, 5.35, 0, f);               // parapet
    for (let x = -5; x <= 5; x += 2.0) box(0.85, 0.85, 4.7, C.stoneDim, x, 5.95, 0, f);  // crenellations
    const tower = (x) => {
      cyl(1.3, 1.4, 8, C.stone, x, 4, 0, f, 12);
      const cone = cyl(0, 1.5, 1.8, 0x7A4A6E, x, 8.8, 0, f, 12);
      for (let i = 0; i < 12; i++) box(0.6, 0.7, 0.35, C.stoneDim, Math.cos(i/12*Math.PI*2)*1.35, 7.7, Math.sin(i/12*Math.PI*2)*1.35, f);
    };
    tower(-5.5); tower(5.5);
    box(2.4, 3.2, 0.25, 0x120E0A, 0, 1.7, 1.85, f);               // dark doorway behind the gate
    this.gate = box(2.6, 3.4, 0.45, C.crustDark, 0, 1.7, 2.1, f);   // the (sealable) gate itself
    box(2.6, 0.6, 0.55, C.stoneDim, 0, 3.4, 2.2, f);               // gate arch
    const sign = textSprite('The Fortress', 30, '#EFE6D0', 4.6, 1.0);
    sign.position.set(0, 8.4, -18); this.scene.add(sign);
    this._refreshFortress();
  }

  // Reflect the fortressOpen flag on the gate mesh: hidden = swung open, revealing
  // the dark doorway. Called on build and whenever a quest turn-in changes it.
  _refreshFortress(){
    if (this.gate) this.gate.visible = !getFlag('fortressOpen');
  }

  _buildGraveyardGate(){
    const g = new THREE.Group(); g.position.set(-18, 0, 4); g.rotation.y = 0.35; this.scene.add(g);
    // iron posts and arch
    cyl(0.09, 0.09, 2.8, 0x1A1A1A, -1.1, 1.4, 0, g, 6);
    cyl(0.09, 0.09, 2.8, 0x1A1A1A,  1.1, 1.4, 0, g, 6);
    const arch = cyl(0.07, 0.07, 2.6, 0x1A1A1A, 0, 2.75, 0, g, 6);
    arch.rotation.z = Math.PI/2;
    const barTop = cyl(0.04, 0.04, 2.2, 0x1A1A1A, 0, 2.25, 0, g, 6);
    barTop.rotation.z = Math.PI/2;
    // mossy stone plinths
    box(0.4, 0.5, 0.4, 0x6A6A62, -1.1, 0.25, 0, g);
    box(0.4, 0.5, 0.4, 0x6A6A62,  1.1, 0.25, 0, g);
    // crooked sign
    const sign = textSprite('Graveyard', 26, '#C5C5C5', 2.6, 0.65);
    sign.position.set(-18, 3.8, 4); this.scene.add(sign);
  }

  _enterGraveyard(){
    this.manager.goTo('graveyard', { from: 'forest' });
  }

  _buildDigSpots(){
    const spots = [ new THREE.Vector3(-12, 0, 3), new THREE.Vector3(12, 0, -10), new THREE.Vector3(-12, 0, -12) ];
    spots.forEach(p => {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.55, 0.07, 8, 20), new THREE.MeshBasicMaterial({ color: 0xF7C948 }));
      ring.rotation.x = -Math.PI/2; ring.position.set(p.x, 0.07, p.z); this.scene.add(ring);
      const glow = sphere(0.16, 0xFFEEA0, p.x, 0.22, p.z, this.scene, 10);
      glow.material = new THREE.MeshBasicMaterial({ color: 0xFFEEA0 });
      p._ring = ring; p._glow = glow;
    });
    this._digSpots = spots;
    return spots;
  }

  _buildKeystoneSpots(){
    this.keystoneSpots = [
      { pos: new THREE.Vector3(-16, 0, -6), id: 0 },
      { pos: new THREE.Vector3(14, 0, -3), id: 1 },
      { pos: new THREE.Vector3(-4, 0, -14), id: 2 }
    ];
    this.keystoneSpots.forEach(s => {
      s.dug = getFlag(`keystoneFragment${s.id}`);
      const g = new THREE.Group(); g.position.copy(s.pos); this.scene.add(g);
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.55, 0.07, 8, 20), new THREE.MeshBasicMaterial({ color: 0xE8C24B }));
      ring.rotation.x = -Math.PI/2; ring.position.set(s.pos.x, 0.07, s.pos.z); this.scene.add(ring);
      const glow = sphere(0.14, 0xE8C24B, s.pos.x, 0.22, s.pos.z, this.scene, 10);
      glow.material = new THREE.MeshBasicMaterial({ color: 0xE8C24B });
      s.ring = ring; s.glow = glow;
      if (s.dug){ ring.visible = false; glow.visible = false; }
    });
  }

  _digKeystone(i){
    const s = this.keystoneSpots[i];
    if (s.dug){ HUD.say('The sigil mound is empty.'); return; }
    s.dug = true;
    setFlag(`keystoneFragment${i}`, true);
    if (s.ring) s.ring.visible = false;
    if (s.glow) s.glow.visible = false;
    this.bread.drawFace('yum');
    HUD.say('You unearth a fragment of the Baker King\'s sigil!');
    progress('keystoneFragment', 1);
  }

  _buildWoodcut(){
    // an extra-large oak by the trail, with a chopping stump + axe beside it
    this.oak = this._tree(12, -6, 2.0);
    this.oakBaseScale = 2.0;
    cyl(0.7, 0.8, 0.8, C.rye, 8.8, 0.4, -4.4, this.scene, 12);   // stump
    cyl(0.72, 0.72, 0.09, 0x8A5A34, 8.8, 0.8, -4.4, this.scene, 12);
    const axe = new THREE.Group(); axe.position.set(8.8, 0.8, -4.4); axe.rotation.z = -0.5; this.scene.add(axe);
    cyl(0.05, 0.05, 1.0, C.cocoa, 0, 0.4, 0, axe, 6);            // handle
    box(0.36, 0.24, 0.07, 0xC7CBD1, 0.02, 0.88, 0, axe);         // head
  }

  _makeCritters(){
    const build = (bodyCol) => (g) => {
      sphere(0.34, bodyCol, 0, 0.36, 0, g, 12);                   // round body
      sphere(0.055, 0x2E2018, -0.12, 0.44, 0.28, g, 8);           // eyes
      sphere(0.055, 0x2E2018, 0.12, 0.44, 0.28, g, 8);
      cyl(0.02, 0.02, 0.18, C.rye, 0, 0.66, 0, g, 5);             // sprout stem
      const leaf = box(0.15, 0.045, 0.11, C.sprout, 0.06, 0.74, 0, g); leaf.rotation.z = 0.5;
      cyl(0.035, 0.035, 0.16, C.limb, -0.12, 0.07, 0, g, 5);      // little legs
      cyl(0.035, 0.035, 0.16, C.limb, 0.12, 0.07, 0, g, 5);
    };
    const specs = [
      { pos: new THREE.Vector3(-8, 0, 6), col: 0x8ECF6A, line: '*rustle rustle*' },
      { pos: new THREE.Vector3(10, 0, 5), col: 0xB6D98A, line: '*chirp!*' },
      { pos: new THREE.Vector3(-6, 0, -10), col: 0x6FB25A, line: 'hoo?' }
    ];
    return specs.map(s => new Townsperson({
      name: 'Critter', line: s.line, pos: s.pos,
      facing: Math.random() * Math.PI * 2, wander: 2.6, speed: 0.7, bubbleY: 1.4,
      build: build(s.col)
    }));
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.3, 0.72];
    HUD.refreshQuests();
    this._toExplore(true);
    this._updateLamps();
  }

  exit(){
    this._teardownGame();
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say(''); HUD.toggleQuestScroll(false);
  }

  _toExplore(first){
    this.state = 'explore';
    this._clearPanels();
    this.bread.resetLimbs();
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('the forest');
    if (first) HUD.say('The Forest! Follow the trail deeper in — there\'s a big oak to chop, sparkly spots to dig, critters about, and an old Fortress at the end of the path.');
    HUD.setHint('click to walk · walk up to the oak, the dig spots, the Fortress, or the trail home');
    HUD.hideHintSoon(8000);
  }

  _tap(ray){
    if (checkNpcClick(ray, this.ranger, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = this._clampToWoods(new THREE.Vector3(p.x, 0, p.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _clampToWoods(p){
    if (p.length() > WALK_R) p.setLength(WALK_R);
    return p;
  }

  // ---- panels (reuse the shared .cafe-* game UI) ----
  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  // ---- Ranger Rusk: quest giver ----
  _talkToRanger(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.ranger.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    if (isActive('rangers-warning')){
      setFlag('heardRangerWarning', true);
      flagObjective('rangers-warning', 'heardRangerWarning');
      HUD.say('Ranger Rusk: "The Fortress gate hums at night. Something inside wants out."');
    }
    this._renderRangerDialog();
  }

  _renderRangerDialog(){
    this._clearPanels();
    const { available, active } = questsForGiver('ranger');
    const rows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isReady(id)){
        rows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>objective complete!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        const p = objectiveProgress(id);
        const label = q.objective.kind === 'flag' ? q.objective.noun : `${p.have} / ${p.goal} ${q.objective.noun}`;
        rows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${label}</span></div>
          <span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      const rew = q.reward?.crumbs ? ` · reward 🍞 ${q.reward.crumbs}` : '';
      rows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}${rew}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });

    const body = rows.length ? rows.join('')
      : `<p class="q-dnone">"That's all I've got for now, partner. Explore a spell and check back!"</p>`;

    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Ranger Rusk</h2>
        <div class="cafe-sub">keeper of the woods</div>
        <div class="q-drows">${body}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this.bread.drawFace('yum'); this._renderRangerDialog(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => this._onTurnIn(b.dataset.id));
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _onTurnIn(id){
    const reward = turnIn(id);
    if (!reward) return;
    HUD.refreshCrumbs();
    HUD.refreshLumber();
    this._refreshFortress();          // haul-lumber turn-in flips fortressOpen
    this.bread.drawFace('yum');
    this._renderRangerDialog();
  }

  // Digging a sparkle spot: turn up a random collectible for the Museum, plus a
  // few crumbs. Each spot gives once per visit (tracked in-memory).
  _dig(spot){
    if (spot._dug){ HUD.say('you already dug here — try another sparkle!'); return; }
    spot._dug = true;
    addCrumbs(3); HUD.refreshCrumbs();
    this.bread.drawFace('yum');

    // sometimes the earth yields a cooking ingredient instead of a collectible
    if (Math.random() < 0.45){
      const pool = ['berry', 'berry', 'mushroom', 'mushroom', 'herb', 'honey'];
      const id = pool[(Math.random() * pool.length) | 0];
      addIngredient(id, 1);
      HUD.say(`You forage ${ingredientName(id)}! Great for cooking.`);
    } else {
      const id = randomFind();
      const item = ITEMS[id];
      addFound(id);
      HUD.say(`You dig up ${item.icon} ${item.name}! (+3 crumbs) — go display it at the Museum in Downtown.`);
    }
    if (spot._ring) spot._ring.visible = false;
    if (spot._glow) spot._glow.visible = false;
  }

  // Walking up to the Fortress: sealed until the bridge quest opens it, then the
  // gate is open — reaching it satisfies the final quest and enters the dungeon.
  _approachFortress(){
    this.bread.drawFace('o');
    if (!getFlag('fortressOpen')){
      HUD.say('The Fortress gate is sealed tight. Ranger Rusk said the old trail bridge needs mending first…');
      return;
    }
    if (!getFlag('fortressInvite')){
      HUD.say('The gate is open, but a guard stops you. "No royal invite, no entry, little loaf."');
      return;
    }
    flagObjective('reach-fortress', 'reachedFortress');
    HUD.say('The great gate stands open. Step inside…');
    this.manager.goTo('fortress', { from: 'forest' });
  }

  // ---- wood-cutting ----
  _startWoodcut(){
    const check = spendForAction('woodcut');
    if (!check.ok){
      HUD.say(`Too tired to chop wood. ${check.reason}`);
      return;
    }
    this.state = 'playing';
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.setHint(''); HUD.hideHint(); HUD.say('');

    this.bread.stop();
    this.bread.root.position.copy(CHOP_SPOT);
    const toOak = new THREE.Vector3().subVectors(OAK_POS, CHOP_SPOT);
    this.bread.root.rotation.y = Math.atan2(toOak.x, toOak.z);
    this.bread.facing = this.bread.root.rotation.y;

    this.woodcut = new Woodcut({
      onChop: (r) => this._onChop(r),
      onFinish: (r) => this._results(r)
    });
    this.woodcut.mount();
    this.woodcut.begin();

    const q = this._panel('<button class="cafe-quit">✕ done</button>', 'bare');
    q.querySelector('.cafe-quit').onclick = () => this.woodcut && this.woodcut._finish();
  }

  _onChop(r){
    this.breadChopT = 0.32;                             // arms swing down
    this.bread.drawFace(r.good ? 'effort' : 'o');
    const n = r.felled ? 10 : (r.good ? 5 : 2);         // wood chips off the trunk
    for (let i = 0; i < n; i++){
      const chip = box(0.11, 0.08, 0.06, i % 2 ? C.cream : C.rye, OAK_POS.x - 0.8, 0.8, OAK_POS.z + 0.5, this.scene);
      const a = Math.random() * Math.PI * 2;
      chip._v = new THREE.Vector3(Math.cos(a) * 1.8, 2.2 + Math.random() * 2.2, Math.sin(a) * 1.8 + 1.2);
      chip._life = 0.7 + Math.random() * 0.3;
      this.chips.push(chip);
    }
    if (r.felled){ this.oakPop = 0.5; this.bread.drawFace('yum'); progress('chop', 1); }
  }

  _updateChips(dt){
    for (let i = this.chips.length - 1; i >= 0; i--){
      const c = this.chips[i];
      c._life -= dt;
      c._v.y -= 9 * dt;                                 // gravity
      c.position.addScaledVector(c._v, dt);
      c.rotation.x += dt * 8; c.rotation.z += dt * 6;
      if (c._life <= 0 || c.position.y < 0){ this.scene.remove(c); this.chips.splice(i, 1); }
    }
  }

  _results(r){
    this.state = 'results';
    this._teardownGame();
    this.bread.resetLimbs(); this.bread.drawFace('grin');
    if (r.lumber > 0) addLumber(r.lumber);
    if (r.crumbs > 0) addCrumbs(r.crumbs);
    HUD.refreshCrumbs();
    HUD.refreshLumber();
    HUD.refreshEnergy();

    const grade = r.treesFelled >= 6 ? 'S' : r.treesFelled >= 4 ? 'A' : r.treesFelled >= 2 ? 'B' : r.treesFelled >= 1 ? 'C' : 'D';
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">${grade}</div>
        <h2>Timber!</h2>
        <div class="cafe-stats">
          <div><b>${r.treesFelled}</b><span>trees</span></div>
          <div><b>${r.cleanChops}</b><span>clean</span></div>
          <div><b>${r.bestStreak}</b><span>best streak</span></div>
          <div><b>${r.goodChops}</b><span>good hits</span></div>
        </div>
        <p class="cafe-payout">🪵 +${r.lumber} lumber · 🍞 +${r.crumbs} crumbs</p>
        <button class="cafe-btn again">↻ chop again</button>
        <button class="cafe-btn ghost leave">← back to the forest</button>
      </div>`);
    p.querySelector('.again').onclick = () => this._startWoodcut();
    p.querySelector('.leave').onclick = () => this._toExplore(false);
  }

  _teardownGame(){
    if (this.woodcut){ this.woodcut.unmount(); this.woodcut = null; }
    this.chips.forEach(c => this.scene.remove(c)); this.chips = [];
    this.breadChopT = 0; this.oakPop = 0;
    if (this.oak) this.oak.scale.setScalar(this.oakBaseScale);
  }

  update(dt){
    const night = isNight();
    this.ambient.update(dt, night ? 'night' : 'day');
    this.weather.update(dt, this.bread.root.position);
    this.critters.forEach(c => c.update(dt, this.bread.root.position));
    this.ranger.update(dt, this.bread.root.position);
    this._updateChips(dt);
    this._updateLamps();

    // twinkle the dig-spot glows
    const tw = 0.5 + 0.5 * Math.sin(performance.now() * 0.004);
    this._digSpots.forEach((p, i) => { if (p._glow) p._glow.position.y = 0.2 + tw * 0.14 * (1 + i * 0.1); });

    if (this.state === 'playing'){
      this.woodcut.update(dt);
      // chop pose: arms swing down on a hit, then ease back up
      if (this.breadChopT > 0){
        this.breadChopT -= dt;
        this.bread.armL.shoulder.rotation.x = 1.4;
        this.bread.armR.shoulder.rotation.x = 1.4;
      } else {
        this.bread.armL.shoulder.rotation.x *= (1 - Math.min(1, dt * 6));
        this.bread.armR.shoulder.rotation.x *= (1 - Math.min(1, dt * 6));
      }
      // oak "pop" when a tree falls: shrink, then spring back = a fresh tree
      if (this.oakPop > 0){
        this.oakPop -= dt;
        const k = Math.max(0, this.oakPop / 0.5);
        this.oak.scale.setScalar(this.oakBaseScale * (0.62 + 0.38 * (1 - k)));
      }
      // locked framing: camera over Bread Boi's shoulder so he isn't lost in trees
      this.camera.position.set(4.2, 3.0, 1.8);
      this.camera.lookAt(6.8, 1.4, -3.6);
      return;
    }

    // explore / results
    if (this.state === 'explore'){
      this.bread.update(dt);
      this._clampToWoods(this.bread.root.position);
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
      if (near && nd < near.r){
        if (this.nearSpot !== near){
          this.nearSpot = near;
          HUD.showEnter(near.label, () => {
            if (near.action) near.action();
            else { HUD.say(near.say); this.bread.drawFace('o'); }
          });
        }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
